import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AlertController, LoadingController, NavController } from '@ionic/angular';
import { ProfileCompanyModel, ProfileService } from '@libs/core/api';
import { AUTHENTICATE_OPTIONS, AuthResultEnum, BaseAuthService, OAuth2AuthenticateOptions, User } from '@libs/core/auth';
import { PrivacyPolicyService } from '@libs/core/privacy-policy';
import { StorageService } from '@libs/core/storage';
import { PlatformDetectionService } from '@libs/util/shared';
import { ReplaySubject, combineLatest, lastValueFrom, map } from 'rxjs';

@Injectable()
export class AuthService extends BaseAuthService<User, User> {
  private storageService: StorageService;

  private readonly privacyPolicyService = inject(PrivacyPolicyService);
  private readonly profileService = inject(ProfileService);
  private readonly alertController = inject(AlertController);
  private readonly loadingController = inject(LoadingController);
  readonly companyIdSessionKey = 'currentCompanyId';

  private currentCompanyIdSubject = new ReplaySubject<string>(1);
  private companiesSubject = new ReplaySubject<ProfileCompanyModel[]>(1);

  readonly companies$ = this.companiesSubject.asObservable();

  readonly currentCompany$ = combineLatest([this.currentCompanyIdSubject, this.companiesSubject]).pipe(
    map(([currentCompanyId, companies]) => companies.find((company) => company.id === currentCompanyId) as ProfileCompanyModel),
  );

  private isHandlingCompanyDisabled = false;

  constructor() {
    const navigationController = inject(NavController);
    const storageService = inject(StorageService);
    const platformService = inject(PlatformDetectionService);
    const authOptions = inject<OAuth2AuthenticateOptions>(AUTHENTICATE_OPTIONS);

    super(storageService, platformService, authOptions, navigationController);

    this.storageService = storageService;
  }

  override mapUser(rawUser: User): User {
    return rawUser;
  }

  override async afterLogin() {
    try {
      const profile = await lastValueFrom(this.profileService.getProfile());

      this.privacyPolicyService.setPrivacyPolicy(profile.hasAcceptedPrivacyPolicy);

      if (profile.companies.length === 0) {
        const loading = await this.loadingController.getTop();
        await loading?.dismiss();

        const alert = await this.alertController.create({
          header: 'Attenzione',
          message: "Nessuna azienda attiva disponibile. Contatta l'amministratore.",
          buttons: ['OK'],
        });

        await alert.present();
        await alert.onWillDismiss();

        await this.logout();
        return AuthResultEnum.NOT_AUTHORIZED;
      }

      let persistedCompanyId = await this.storageService.get(this.companyIdSessionKey);

      if (!persistedCompanyId) {
        persistedCompanyId = profile.companies[0].id;
      }

      if (!profile.companies.some((company) => company.id === persistedCompanyId)) {
        persistedCompanyId = profile.companies[0].id;
      }

      await this.storageService.set(this.companyIdSessionKey, persistedCompanyId);

      this.companiesSubject.next(profile.companies);
      this.currentCompanyIdSubject.next(persistedCompanyId);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 403) {
        const alert = await this.alertController.create({
          message: "Non ci sono aziende associate al tuo account. Contatta l'amministratore.",
          buttons: ['OK'],
        });

        const loading = await this.loadingController.getTop();

        await loading?.dismiss();

        await alert.present();
        await alert.onWillDismiss();

        await this.logout();

        return AuthResultEnum.NOT_AUTHORIZED;
      }
    }
    return;
  }

  async handleCompanyDisabled(): Promise<void> {
    if (this.isHandlingCompanyDisabled) {
      return;
    }

    this.isHandlingCompanyDisabled = true;

    try {
      const profile = await lastValueFrom(this.profileService.getProfile());
      const companies = profile.companies;

      if (companies.length > 0) {
        const newCompany = companies[0];

        const alert = await this.alertController.create({
          header: 'Attenzione',
          message: `L'azienda selezionata è stata disattivata. Passaggio all'azienda ${newCompany.name}.`,
          buttons: ['OK'],
        });

        await alert.present();
        await alert.onWillDismiss();

        await this.storageService.set(this.companyIdSessionKey, newCompany.id);
        window.location.href = '/';
      } else {
        const alert = await this.alertController.create({
          header: 'Attenzione',
          message: "L'azienda selezionata è stata disattivata. Non ci sono altre aziende disponibili.",
          buttons: ['OK'],
        });

        await alert.present();
        await alert.onWillDismiss();

        await this.logout();
      }
    } finally {
      this.isHandlingCompanyDisabled = false;
    }
  }

  changeCompany(companyId: string) {
    this.storageService.set(this.companyIdSessionKey, companyId).then(() => {
      this.currentCompanyIdSubject.next(companyId);
    });
  }
}
